/**
 * @fileoverview Plain-language automation summary (MOR-736)
 *
 * Renders a rule as a single readable sentence — "When the day-one status
 * changes to [In review], send a notification to [Elle Curran]." — with badges
 * only on value-like elements (statuses and workflow roles in their assigned
 * colour, users / task titles / priorities neutral). Resolves status keys, user
 * ids and role ids to display names so the summary reads naturally instead of
 * showing raw keys.
 *
 * Shared by the workflow editor's automations section, the automations manager's
 * grouped workflow view, the workflow item card and the task side panel.
 */

import { Fragment, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import type {
  AutomationAction,
  AutomationTrigger,
} from "@cvx/automations/automationRules/schema";
import { Badge } from "@/ui/primitives";
import type { BadgeColor } from "@/ui/primitives/badge";
import { getDisplayName } from "@/ui/composed";
import { cn } from "@/utils/misc";
import { ruleSummaryTokens, type TriggerToken } from "./automation-config";

/** Punctuation-leading text tokens hug the previous word (no space before). */
function needsLeadingSpace(token: TriggerToken): boolean {
  return !(token.kind === "text" && /^[,.]/.test(token.text));
}

export function AutomationRuleSummary({
  orgId,
  trigger,
  actions,
  className,
}: {
  orgId: Id<"organizations">;
  trigger: AutomationTrigger;
  actions: AutomationAction[];
  className?: string;
}) {
  const { data: statuses } = useQuery(
    convexQuery(api.tasks.statuses.listByOrg, { orgId }),
  );
  const { data: members } = useQuery(
    convexQuery(api.organizations.queries.getOrganizationMembers, { orgId }),
  );
  const { data: roles } = useQuery(
    convexQuery(api.tasks.workflowRoles.listByOrg, { orgId }),
  );

  const resolveStatus = useMemo(() => {
    const map = new Map(statuses?.map((s) => [s.key, s]) ?? []);
    return (key: string) => {
      const status = map.get(key);
      return status
        ? { name: status.name, color: status.color as BadgeColor | undefined }
        : undefined;
    };
  }, [statuses]);

  const resolveUser = useMemo(() => {
    const map = new Map(members?.map((m) => [m.user._id, m.user]) ?? []);
    return (userId: string) => {
      const user = map.get(userId as Id<"users">);
      return user
        ? getDisplayName(user.firstName, user.lastName, user.email)
        : undefined;
    };
  }, [members]);

  const resolveRole = useMemo(() => {
    const map = new Map(roles?.map((r) => [r._id, r]) ?? []);
    return (roleId: string) => {
      const role = map.get(roleId as Id<"workflowRoles">);
      return role
        ? { name: role.name, color: role.color as BadgeColor | undefined }
        : undefined;
    };
  }, [roles]);

  const tokens = useMemo(
    () =>
      ruleSummaryTokens(
        { trigger, actions },
        resolveStatus,
        resolveUser,
        resolveRole,
      ),
    [trigger, actions, resolveStatus, resolveUser, resolveRole],
  );

  return (
    <div
      className={cn("text-xs leading-relaxed text-muted-foreground", className)}
    >
      {tokens.map((token, i) => {
        const space = i > 0 && needsLeadingSpace(token) ? " " : "";
        if (token.kind === "text") {
          return (
            <span key={i}>
              {space}
              {token.text}
            </span>
          );
        }
        return (
          <Fragment key={i}>
            {space}
            <Badge
              variant="soft"
              color={token.color}
              className="mx-px inline-flex align-middle font-normal"
            >
              {token.label}
            </Badge>
          </Fragment>
        );
      })}
    </div>
  );
}
